import { Injectable } from "@angular/core";
import { combineLatest, Observable } from "rxjs";
import { NotificationsRemote } from "@modules/notifications/domain/infrastructure/notifications.remote";
import {
	BPGlobalConfig,
	NotificationsCountModel,
	NotificationsIconModel,
	NotificationsModel
} from "@modules/notifications/domain/entities/notifications.model";
import { map, shareReplay, skip, take, withLatestFrom } from "rxjs/operators";
import { NotificationsPaginationService } from "@modules/notifications/domain/infrastructure/notifications-pagination.service";
import { NotificationsStateService } from "@modules/notifications/domain/+state/notifications.state.service";
import { NOTIFICATIONS_PER_PAGE } from "@helpers/constants";
import { SocketService } from "@main-services/socket.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { NotificationSocketEvent } from "@models/transaction/messaging-socket-events";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { SegmentService } from "@main-services/segment.service";
import { ActiveToast, ToastrService } from "ngx-toastr";
import { cloneDeep } from "@apollo/client/utilities";
import { BpGlobalNotificationsComponent } from "@modules/shared/bp-global-notifications/bp-global-notifications.component";
import { NotificationsRoutingFacade } from "@modules/notifications/domain/+state/notifications-routing-facade.service";
import { RudderstackService } from "@main-services/rudderstack.service";

@UntilDestroy()
@Injectable({
	providedIn: "root"
})
export class NotificationsFacade {
	private icons$: Observable<NotificationsIconModel[]> = this.remote
		.getIcons$()
		.pipe(shareReplay({ bufferSize: 1, refCount: true }));
	public newGlobalNotifications$ = this.launchDarklyService.isActive$(LD_FLAG.NEW_GLOBAL_NOTIFICATIONS);

	public constructor(
		private readonly remote: NotificationsRemote,
		private readonly paginationService: NotificationsPaginationService,
		private readonly notificationsState: NotificationsStateService,
		private readonly socketService: SocketService,
		private readonly segmentService: SegmentService,
		private readonly rudderstackService: RudderstackService,
		private readonly launchDarklyService: LaunchDarklyService,
		private readonly notificationsRoutingFacade: NotificationsRoutingFacade,
		private readonly toastr: ToastrService
	) {
		this.fetchNotificationsCount();
		this.getNotificationsIcons$();

		this.socketService
			.joinRoom<NotificationSocketEvent>({
				name: "messaging_service",
				type: NotificationSocketEvent,
				isPrivate: true
			})
			.pipe(withLatestFrom(this.newGlobalNotifications$), untilDestroyed(this))
			.subscribe(([event, newGlobalNotificationFF]) => {
				this.notificationsState.saveState({
					count: event.unseen_count
				});

				newGlobalNotificationFF && this.displayToastMessage(event);
			});
	}

	private fetchNotificationsCount(): void {
		this.remote
			.getCountNotifications$()
			.pipe(take(1))
			.subscribe((count) => {
				this.notificationsState.saveState(count);
			});
	}

	public getState$(): Observable<NotificationsCountModel> {
		return this.notificationsState.getState$();
	}

	public getNotificationsIcons$(): Observable<NotificationsModel[]> {
		return combineLatest([this.getList$().pipe(skip(1)), this.icons$]).pipe(
			map(([notifications, icons]: [NotificationsModel[], NotificationsIconModel[]]) => {
				notifications.forEach((n) => {
					const icon = icons.find((i) => n.event.name === i.name);
					n.icon_name = icon.icon_name;
					n.state = icon.state;
				});

				return notifications;
			})
		);
	}

	public initList(): void {
		this.paginationService.initList(NOTIFICATIONS_PER_PAGE);
		this.rudderstackTracking("Notification Centre Screen Viewed");
	}

	public getList$(): Observable<NotificationsModel[]> {
		return this.paginationService.getListObservable();
	}

	public fetchNextPage(): void {
		this.paginationService.fetchListUpdate(NOTIFICATIONS_PER_PAGE);
	}

	public getLoadingObservable$(): Observable<boolean> {
		return this.paginationService.getLoadingObservable();
	}

	public getErrorObservable$(): Observable<boolean> {
		return this.paginationService.getFetchErrorObservable();
	}

	public handleDialogClose(): void {
		this.paginationService.resetList();
		this.paginationService.clearEndOfList();
		this.updateNotificationsCounter();
	}

	public setNotificationsAsSeen$(): Observable<boolean> {
		return this.remote.setNotificationsAsSeen$();
	}

	public isEndOfList$(): Observable<boolean> {
		return combineLatest([this.getNotificationsIcons$(), this.paginationService.isEndOfList$]).pipe(
			map(
				([notificationList, endOfList]: [NotificationsModel[], boolean]) =>
					notificationList.length > 0 && endOfList
			)
		);
	}

	public openGlobalToast(
		state: string,
		subtitle: string,
		title: string,
		iconName: string,
		showAction = false,
		actionMessage: string = ""
	): ActiveToast<void> {
		const options: BPGlobalConfig = cloneDeep(this.toastr.toastrConfig);

		options.toastComponent = BpGlobalNotificationsComponent;
		options.toastClass = "new-toast toastr-notification-" + state;
		options.closeButton = true;
		options.messageClass = iconName;
		options.showAction = showAction;
		options.actionMessage = actionMessage;

		return this.toastr.show(subtitle, title, options, state);
	}

	public displayToastMessage(event: NotificationSocketEvent): void {
		const state = event.icon.state;
		const title = event.event.title;
		const subtitle = event.event.subtitle;
		const eventName = event.event.name;
		const iconName = event.icon.icon_name;
		const skipGlobal = event.skip_global_notification;

		if (skipGlobal) {
			return; // skip showing the global notification
		}

		const ref = this.openGlobalToast(
			state,
			subtitle,
			title,
			iconName,
			event.event.showAction ?? false,
			event.event.actionMessage ?? ""
		);

		this.rudderstackTracking("Global Notification Displayed", eventName);

		if (event.event?.target_path) {
			ref.onTap.pipe(take(1)).subscribe(() => {
				this.rudderstackTracking("Global Notification Clicked", eventName);
				this.notificationsRoutingFacade.getTarget(event.event.target_path, event.pid);
				this.updateNotificationsCounter();
			});
		}
		ref.onAction
			.pipe(take(1))
			.subscribe(() => this.rudderstackTracking("Global Notification Close Icon Clicked", eventName));
	}

	public updateNotificationsCounter(): void {
		this.setNotificationsAsSeen$()
			.pipe(take(1))
			.subscribe(() => this.fetchNotificationsCount());
	}

	public rudderstackTracking(trackingName: string, eventName?: string): void {
		this.segmentService.track({
			name: trackingName,
			properties: {
				event_name: eventName
			}
		});
		this.rudderstackService.track({
			name: trackingName,
			properties: {
				event_name: eventName
			}
		});
	}
}
